<script setup lang='ts'>
import { Head, Link, router } from '@inertiajs/vue3'
import Button from '@/Components/Utils/Button.vue'
import MultiSelect from 'primevue/multiselect'
import Dialog from 'primevue/dialog'
import { useFormatTime } from '@/Composables/useFormatTime'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { inject, ref } from 'vue'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'
import { useTruncate } from '@/Composables/useTruncate'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'
import Tag from '@/Components/Utils/Tag.vue'
import { routeType } from '@/types/route'
import { aikuLocaleStructure } from '@/Composables/useLocaleStructure'
import Icon from '@/Components/Utils/Icon.vue'
import { DatePicker } from 'primevue'
import CountUp from 'vue-countup-v3'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFilePdf, faTimes, faArrowRight, faArrowLeft, faPencil, faTrashUndo, faCalendarAlt, faDollarSign, faMoneyBillWave } from '@fal'
import { faBadgePercent, faTag } from '@fas'
import { library } from '@fortawesome/fontawesome-svg-core'
import { layoutStructure } from '@/Composables/useLayoutStructure'
library.add(faFilePdf, faTimes, faArrowRight, faArrowLeft, faPencil, faTrashUndo, faCalendarAlt, faDollarSign, faTag, faMoneyBillWave, faBadgePercent)


interface Order {
    id: number
    reference: string
    total: number
    product: {
        id: number
        name: string
        price: number
        type_icon: {
            tooltip?: string
            icon: string | string[]
            class?: string
        }
    }
    is_have_discount: boolean
    product_price_show: number
    quantity_show: number
    total_amount_show: number
    quantity: number
    total_amount: number
}
interface Invoice {
    id: number
    orders: Order[]
    reference: string
    total_amount: number
    created_at: string
    sales: {
        id: number
        name: string
    }
    billing_date: string | null
    type: string
    type_label: string
}

const props = defineProps<{
    invoice: Invoice
    invoiceCross?: Invoice
}>()

const locale = inject('locale', aikuLocaleStructure)
const layout = inject('layout', layoutStructure)

// const generateLinkOrder = (slug?: string) => {
//     if (!slug) return null
//     switch (layout?.auth?.role) {
//         case 'super_admin':
//         case 'admin':
//             return route('casheer.orders.show', slug)
//         case 'sales':
//             return route('sales.orders.show', slug)
//         default:
//             return null
//     }
// }
</script>

<template>
    <section v-if="invoice?.orders?.length" aria-labelledby="summary-heading" class="">
        <h2 id="summary-heading">
            Pembayaran: <span class="font-bold mr-2" :class="invoice.billing_date ? 'text-green-500' : ''">{{ invoice.type_label }}</span>
            <Tag v-if="invoice.billing_date" label="Lunas" :theme="3" />
        </h2>

        <h2 id="summary-heading" class="">Jumlah order: <span class="font-bold">{{ invoice.orders.length }}</span></h2>

        <div class="mt-3">
            <ul role="list" class="border border-gray-200 text-sm font-medium">
                <li
                    v-for="order, idxOrder in invoice.orders"
                    :key="order.id"
                    class="first:border-t-0 border-t border-t-gray-300 relative py-3 flex flex-col lg:flex-row gap-x-3 pr-2"
                    :cxxlass="order.is_have_discount ? 'border-l-4 border-l-sky-300 bg-sky-50' : ''"
                >
                    <div class="px-3 flex-grow grid lg:grid-cols-7 gap-x-4 ">
                        <div class="md:col-span-3 flex gap-x-2">
                            <div class="text-gray-500">{{ idxOrder+1 }}.</div>
                            <div class="space-y-0.5">
                                <h3 class="font-bold flex">
                                    <Link :href="route('sales.orders.show', order.slug)" class="primaryLink">
                                        {{ order.reference }}
                                    </Link>
                                    <!-- <span v-else>
                                        {{ order.reference }}
                                    </span> -->
                                </h3>

                                <!-- Product name -->
                                <p class="text-gray-500">
                                    <Icon :data="order.product?.type_icon" />
                                    {{ order.product?.name }}
                                </p>

                                <p class="lg:hidden text-gray-500 tabular-nums">
                                    <FontAwesomeIcon icon='fal fa-dollar-sign' class='' fixed-width aria-hidden='true' />
                                    <span :class="order.quantity_show !== order.quantity_by_type ? 'text-sky-600' : ''">{{ locale.number(order.quantity_show) }} pcs</span>, <span :class="order.product_price_show !== order.product?.price ? 'text-sky-600' : ''">@{{ useFormatCurrency(order.product_price_show) }}</span>
                                </p>

                                <p class="text-gray-500">
                                    <FontAwesomeIcon icon='fal fa-copyright' class='' fixed-width aria-hidden='true' />
                                    {{ order.brand?.name ?? '-' }}
                                </p>
                                
                                <p class="text-gray-500 italic">
                                    <FontAwesomeIcon icon='fal fa-calendar-alt' class='' fixed-width aria-hidden='true' />
                                    {{ useFormatTime(order.created_at) }}
                                </p>

                                <div class="">
                                    <FontAwesomeIcon icon='fal fa-money-bill-wave' class='' fixed-width aria-hidden='true' />
                                    <span class="ml-1" :class="order.discount_in_percentage > 0 ? 'line-through text-gray-400' : ''">{{ useFormatCurrency(order.total_amount_show) }}</span>
                                    <span v-if="order.discount_in_percentage > 0" class="ml-1 text-sky-600 font-semibold">
                                        {{ useFormatCurrency(order.final_total ?? 0) }}
                                    </span>
                                    
                                    <div class="ml-1 inline">
                                        <Tag v-if="order.discount_in_percentage > 0" xlabel="order.discount_in_percentage" :theme="17" icon="fal fa-badge-percent" noHoverColor>
                                            <template #label>
                                                <FontAwesomeIcon icon='fas fa-tag' class='' fixed-width aria-hidden='true' />
                                                {{ order.discount_in_percentage }}%
                                            </template>
                                        </Tag>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <p class="hidden lg:block flex-none text-right font-medium tabular-nums">
                            <template v-if="order.product_price_show !== order.product?.price">
                                <span class="line-through text-gray-400" >
                                    {{ useFormatCurrency(order.product?.price ?? 0) }}<span class="text-gray-400 font-normal">/pcs</span>
                                </span>
                                <span class="block text-sky-600">
                                    {{ useFormatCurrency(order.product_price_show) }}<span class="text-gray-400 font-normal">/pcs</span>
                                </span>
                            </template>

                            <template v-else>
                                {{ useFormatCurrency(order.product_price_show) }}<span class="text-gray-400 font-normal">/pcs</span>
                            </template>
                        </p>

                        <p class="hidden lg:block flex-none text-right font-medium tabular-nums">
                            <template v-if="order.quantity_show !== order.quantity_by_type">
                                <span class="line-through text-gray-400" >
                                    {{ locale.number(order.quantity_by_type) }} pcs
                                </span>
                                <br>
                                <span class="text-sky-600">
                                    {{ locale.number(order.quantity_show) }} pcs
                                </span>
                            </template>

                            <template v-else>
                                {{ locale.number(order.quantity_show) }} pcs
                            </template>
                        </p>
                    </div>
                </li>
            </ul>

            <!-- Section: Total -->
            <dl class="flex items-center justify-between py-3 text-lg font-medium px-3">
                <dt class="">Total</dt>
                <dd class="font-bold tabular-nums">
                    <CountUp :endVal="invoice.final_total || 0" :duration="1.2" :scrollSpyOnce="true"
                        :options="{
                        formattingFn: (number: number) => useFormatCurrency(number)
                    }" />
                </dd>
            </dl>
        </div>          
    </section>
</template>